import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  Avatar,
  Paper,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  Sports as SportsIcon
} from '@mui/icons-material';
import { ChatService } from '../../services/chatService';
import { useAuth } from '../../contexts/AuthContext';
import { SportIcon } from '../../utils/sportIcons';

const GameChatDialog = ({ open, onClose, game }) => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  // Chat layer stack (see chatColorsFor in App.js): recessed area,
  // saturated own bubbles with white text, paper other bubbles.
  const chat = theme.palette.chat;
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);

  // Generate game ID and info
  const gameId = game ? ChatService.generateGameId(game) : null;
  const gameInfo = game ? ChatService.getGameInfo(game) : null;

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Subscribe to messages when dialog opens
  useEffect(() => {
    if (open && gameId && currentUser) {
      setLoading(true);
      setError('');

      const setupChat = async () => {
        try {
          // Create or get the chat channel
          await ChatService.getOrCreateGameChannel(gameId, gameInfo);
          
          // Add user to channel (for tracking participation)
          await ChatService.addUserToChannel(
            gameId, 
            currentUser.uid, 
            currentUser.displayName || currentUser.email
          );
          
          // Mark as read when opening
          await ChatService.markAsRead(gameId, currentUser.uid);
          
          // Subscribe to messages
          unsubscribeRef.current = ChatService.subscribeToMessages(
            gameId, 
            (newMessages) => {
              setMessages(newMessages);
              setLoading(false);
            }
          );
        } catch (err) {
          console.error('Error setting up chat:', err);
          setError('Failed to load chat. Please try again.');
          setLoading(false);
        }
      };

      setupChat();
    }

    // Cleanup subscription when dialog closes
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [open, gameId, currentUser]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setNewMessage('');
      setError('');
      setLoading(false);
    }
  }, [open]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending || !currentUser) return;

    setSending(true);
    setError('');

    try {
      await ChatService.sendMessage(
        gameId,
        currentUser.uid,
        currentUser.displayName || currentUser.email,
        newMessage
      );
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLikeMessage = async (messageId) => {
    if (!currentUser) return;

    try {
      await ChatService.toggleMessageLike(gameId, messageId, currentUser.uid);
    } catch (err) {
      console.error('Error liking message:', err);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  if (!game) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={window.innerWidth < 600}
      PaperProps={{
        sx: {
          height: { xs: '100%', sm: '80vh' },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SportsIcon sx={{ color: chat.headerIcon }} />
            <Box>
              <Typography variant="h6" component="div">
                {game.home_team} vs {game.away_team}
              </Typography>
              <Box display="flex" gap={1} mt={0.5}>
                <Chip 
                  icon={<SportIcon sportTitle={game.sport_title} fontSize="small" />}
                  label={game.sport_title} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ color: chat.chipAccent, borderColor: chat.chipAccent }} 
                />
                <Chip 
                  label={new Date(game.commence_time).toLocaleDateString()} 
                  size="small" 
                  variant="outlined" 
                />
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Messages Area */}
      <DialogContent sx={{ flexGrow: 1, p: 0, overflow: 'hidden', backgroundColor: chat.area }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ 
            flexGrow: 1, 
            overflow: 'auto', 
            maxHeight: '100%',
            p: 1
          }}>
            {messages.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="text.secondary">
                  No messages yet. Start the conversation!
                </Typography>
              </Box>
            ) : (
              messages.map((message) => (
                <ListItem key={message.id} sx={{ px: 1, py: 0.5, alignItems: 'flex-start' }}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 1.5, 
                      width: '100%',
                      backgroundColor: message.userId === currentUser?.uid 
                        ? chat.bubbleOwn 
                        : chat.bubbleOther,
                      color: message.userId === currentUser?.uid 
                        ? chat.bubbleOwnText 
                        : chat.bubbleOtherText
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          fontSize: '0.875rem',
                          bgcolor: message.userId === currentUser?.uid 
                            ? chat.avatarOwn 
                            : chat.avatarOther
                        }}
                      >
                        {message.userName?.charAt(0).toUpperCase() || '?'}
                      </Avatar>
                      
                      <Box flexGrow={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {message.userName || 'Anonymous'}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ color: message.userId === currentUser?.uid ? chat.bubbleOwnMeta : theme.palette.text.secondary }}
                          >
                            {formatTime(message.timestamp)}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {message.message}
                        </Typography>
                        
                        {(message.likes > 0 || message.likedBy?.includes(currentUser?.uid)) && (
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleLikeMessage(message.id)}
                              color={message.likedBy?.includes(currentUser?.uid) ? 'primary' : 'default'}
                              sx={message.userId === currentUser?.uid ? {
                                backgroundColor: 'rgba(255, 255, 255, 0.18)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.30)' }
                              } : undefined}
                            >
                              {message.likedBy?.includes(currentUser?.uid) ? (
                                <ThumbUpIcon fontSize="small" />
                              ) : (
                                <ThumbUpOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                            {message.likes > 0 && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ color: message.userId === currentUser?.uid ? chat.bubbleOwnMeta : theme.palette.text.secondary }}
                              >
                                {message.likes}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </ListItem>
              ))
            )}
            <div ref={messagesEndRef} />
          </List>
        )}
      </DialogContent>

      {/* Message Input */}
      <Divider />
      <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
        <Box display="flex" width="100%" gap={1}>
          <TextField
            fullWidth
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={sending || !currentUser}
            multiline
            maxRows={3}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: chat.inputBg } }}
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending || !currentUser}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            {sending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </Button>
        </Box>
        
        {!currentUser && (
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Please log in to participate in the chat
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GameChatDialog;
